import React from 'react';
import { Alert, Button, List, Space, Spin, Tag, Typography } from 'antd';
import { RightOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from './EmptyQueueState';
import type { SalesAttentionBriefResult } from '@/services/salesDashboard.service';

const { Text } = Typography;

const severityColor: Record<string, string> = {
  critical: 'red',
  attention: 'orange',
  info: 'blue',
};

export const SalesAiBriefStrip: React.FC<{
  data?: SalesAttentionBriefResult;
  loading?: boolean;
  error?: string | null;
  formatCurrency?: (v: number) => string;
  onSectionNavigate?: (section: string) => void;
}> = ({ data, loading, error, formatCurrency, onSectionNavigate }) => {
  const navigate = useNavigate();

  const go = (linkPath: string, section?: string | null) => {
    if (section && onSectionNavigate) {
      onSectionNavigate(section);
      return;
    }
    if (section && linkPath.includes('/growth/dashboard')) {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    navigate(linkPath);
  };

  if (loading && !data) {
    return (
      <ControlTowerSectionCard zone="attention" sectionId="attention-brief" title="Attention Brief">
        <Spin />
      </ControlTowerSectionCard>
    );
  }

  if (error) {
    return (
      <ControlTowerSectionCard zone="attention" sectionId="attention-brief" title="Attention Brief">
        <Alert type="warning" showIcon message={error} />
      </ControlTowerSectionCard>
    );
  }

  const items = data?.items ?? [];
  const highlights = data?.highlights ?? [];

  return (
    <ControlTowerSectionCard
      zone="attention"
      sectionId="attention-brief"
      title={
        <Space>
          <ThunderboltOutlined />
          Attention Brief
          {data?.generated_at ? (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              as of {new Date(data.generated_at).toLocaleString()}
            </Text>
          ) : null}
        </Space>
      }
    >
      {highlights.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          {highlights.map((h) => (
            <Tag key={h} color="geekblue">
              {h}
            </Tag>
          ))}
        </Space>
      )}
      {!items.length ? (
        <EmptyQueueState message="No urgent commercial, cash, or fulfilment items right now." />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Button
                  key="go"
                  type="link"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => go(a.link_path, a.section)}
                >
                  {a.action_label}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space wrap>
                    <Tag color={severityColor[a.severity] || 'default'}>{a.severity}</Tag>
                    <Text strong>{a.title}</Text>
                    {a.count != null ? <Tag>{a.count}</Tag> : null}
                    {a.amount != null && formatCurrency ? (
                      <Text type="secondary">{formatCurrency(a.amount)}</Text>
                    ) : null}
                  </Space>
                }
                description={
                  <Text type="secondary">
                    Why: {a.reason}
                    {a.owner_hint ? ` · ${a.owner_hint}` : ''}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </ControlTowerSectionCard>
  );
};

export default SalesAiBriefStrip;
